import { Inject, Injectable, NgZone } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { BehaviorSubject, filter, of } from 'rxjs';
import { from } from 'rxjs/internal/observable/from';
import { OktaTokensEnum } from '../models/okta-types';
import * as i0 from "@angular/core";
import * as i1 from "@okta/okta-angular";
/**
 * TcpOktaService - Manages Okta authentication and automatic token renewal.
 *
 * Key Features:
 * - Proactive token renewal (5 min before expiry via setTimeout)
 * - Periodic heartbeat check (every 1 min via setInterval)
 * - Tab visibility handler (renews tokens when user returns to idle tab)
 * - Retry mechanism (up to 3 attempts on renewal failure)
 * - Session expiry detection (redirects to login when Okta session expires)
 */
export class TcpOktaService {
    /** Debug counter to track service instantiation */
    static { this.instanceCount = 0; }
    constructor(oktaAuthService, _oktaStateService, ngZone) {
        this.oktaAuthService = oktaAuthService;
        this._oktaStateService = _oktaStateService;
        this.ngZone = ngZone;
        /** Emits new access token string when renewed */
        this.tokenRenewalSub = new BehaviorSubject(undefined);
        /** Emits new ID token string when renewed */
        this.idTokenRenewalSub = new BehaviorSubject(undefined);
        /** Stores separate timeout IDs for each token type (accessToken, idToken) to prevent overwriting */
        this.tokenRenewalTimeoutIds = new Map();
        /** Interval ID for periodic token validity check (handles browser throttling) */
        this.periodicCheckIntervalId = null;
        /** Reference to visibility change handler for cleanup on destroy */
        this.visibilityChangeHandler = null;
        /** Time buffer before token expiry to trigger renewal (5 minutes) */
        // For testing: Change to 3480000 to trigger renewal ~2 min after login
        this.RENEWAL_BUFFER_MS = 300000;
        /** Interval for periodic token validity check (1 minute) */
        this.PERIODIC_CHECK_INTERVAL_MS = 60000;
        /** Maximum retry attempts for token renewal on failure */
        this.MAX_RETRIES = 3;
        /** Delay between retry attempts (5 seconds) */
        this.RETRY_DELAY_MS = 5000;
        TcpOktaService.instanceCount++;
        console.log(`TcpOktaService constructor called. Instance count: ${TcpOktaService.instanceCount}`);
        this.setupTokenRenewalListener();
        this.initializeTokens();
        this.setupVisibilityChangeListener();
        this.startPeriodicTokenCheck();
        console.log('TcpOktaService initialized');
        this.handleCustomCallback();
    }
    /** Cleanup method - clears all listeners, timeouts, and intervals */
    ngOnDestroy() {
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
            this.visibilityChangeHandler = null;
        }
        this.tokenRenewalTimeoutIds.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.tokenRenewalTimeoutIds.clear();
        if (this.periodicCheckIntervalId) {
            clearInterval(this.periodicCheckIntervalId);
            this.periodicCheckIntervalId = null;
        }
        console.log('TcpOktaService destroyed and cleaned up');
    }
    /** Initiates Okta sign-in redirect flow */
    login() {
        this.oktaAuthService.signInWithRedirect();
    }
    /** Signs out the user from Okta */
    logout() {
        this.oktaAuthService.signOut();
    }
    /** Returns current user claims from Okta */
    getUser() {
        return from(this.oktaAuthService.getUser());
    }
    /** Checks if user is currently authenticated with Okta */
    isAuthenticated() {
        return this.oktaAuthService.isAuthenticated();
    }
    /** Returns access token, renewing if expired or expiring soon */
    getToken() {
        console.log('getToken');
        return from(this.ensureTokenRenewal(OktaTokensEnum.accessToken));
    }
    /** Returns ID token, renewing if expired or expiring soon */
    getIdToken() {
        console.log('getIDToken');
        return from(this.ensureTokenRenewal(OktaTokensEnum.idToken));
    }
    /**
     * Retrieves a fresh accessToken or idToken on demand.
     * @returns Promise<AccessToken | IDToken> - The renewed accessToken or idToken based on tokenType.
     */
    async getNewToken(tokenType) {
        try {
            const token = await this.oktaAuthService.tokenManager.renew(tokenType);
            console.log(`${tokenType} successfully renewed:`, token);
            return token;
        }
        catch (error) {
            console.error(`Error renewing ${tokenType}:`, error);
            throw error;
        }
    }
    /** Observable that emits when access token is renewed */
    tokenRenewed() {
        console.log('token renewed');
        return this.tokenRenewalSub.asObservable();
    }
    /** Observable that emits when ID token is renewed */
    idTokenRenewed() {
        console.log('idToken renewed');
        return this.idTokenRenewalSub.asObservable();
    }
    /** Returns Okta authentication state observable */
    getAuthenticationStatus() {
        return this._oktaStateService.authState$;
    }
    /** Listens for Okta SDK 'renewed' events and emits to BehaviorSubjects */
    setupTokenRenewalListener() {
        this.oktaAuthService.tokenManager.on('renewed', (key, newToken) => {
            if (key === OktaTokensEnum.accessToken) {
                console.log('Okta accessToken renewed:', newToken);
                this.tokenRenewalSub.next(newToken.accessToken);
            }
            else if (key === OktaTokensEnum.idToken) {
                console.log('Okta idToken renewed:', newToken);
                this.idTokenRenewalSub.next(newToken.idToken);
            }
        });
    }
    /** Gets existing tokens from Okta and schedules their renewal */
    initializeTokens() {
        const tokenTypes = [OktaTokensEnum.accessToken, OktaTokensEnum.idToken];
        for (const tokenType of tokenTypes) {
            this.oktaAuthService.tokenManager.get(tokenType).then(token => {
                if (token) {
                    this.scheduleTokenRenewal(token, tokenType);
                }
            });
        }
    }
    /**
     * Sets up a listener for visibility changes to handle backgrounded tabs.
     * When the tab becomes visible, immediately checks and renews tokens if needed.
     */
    setupVisibilityChangeListener() {
        this.visibilityChangeHandler = () => {
            if (document.visibilityState === 'visible') {
                console.log('Tab became visible - checking token validity');
                this.ngZone.run(() => {
                    this.checkAndRenewTokensOnVisibility();
                });
            }
        };
        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    /**
     * Starts a periodic check(every 1 min) for token validity.
     * This handles cases where setTimeout is throttled by the browser.
     */
    startPeriodicTokenCheck() {
        if (this.periodicCheckIntervalId) {
            clearInterval(this.periodicCheckIntervalId);
        }
        this.periodicCheckIntervalId = setInterval(() => {
            this.ngZone.run(() => {
                this.checkTokensAndRenewIfNeeded();
            });
        }, this.PERIODIC_CHECK_INTERVAL_MS);
        console.log(`Started periodic token check every ${this.PERIODIC_CHECK_INTERVAL_MS / 1000} seconds`);
    }
    /** Checks and renews tokens when tab becomes visible after being in background */
    async checkAndRenewTokensOnVisibility() {
        const tokenTypes = [OktaTokensEnum.accessToken, OktaTokensEnum.idToken];
        for (const tokenType of tokenTypes) {
            try {
                const token = await this.oktaAuthService.tokenManager.get(tokenType);
                const { isValid, timeRemainingMs } = this.getTokenValidityInfo(token);
                const minutes = Math.floor(timeRemainingMs / 60000);
                const seconds = Math.floor((timeRemainingMs % 60000) / 1000);
                console.log(`[Visibility Check] ${tokenType} - Valid: ${isValid}, Time remaining: ${minutes}m ${seconds}s`);
                if (!isValid || timeRemainingMs < this.RENEWAL_BUFFER_MS) {
                    console.warn(`${tokenType} expires soon or invalid. Renewing now.`);
                    await this.renewWithRetry(tokenType);
                }
                else {
                    this.scheduleTokenRenewal(token, tokenType);
                }
            }
            catch (error) {
                console.error(`Error checking ${tokenType} on visibility change:`, error);
                this.handleRenewalError(error);
            }
        }
    }
    /** Periodic heartbeat check - renews tokens if expiring within buffer time */
    async checkTokensAndRenewIfNeeded() {
        const tokenTypes = [OktaTokensEnum.accessToken, OktaTokensEnum.idToken];
        for (const tokenType of tokenTypes) {
            try {
                const token = await this.oktaAuthService.tokenManager.get(tokenType);
                const { isValid, timeRemainingMs } = this.getTokenValidityInfo(token);
                if (!isValid || timeRemainingMs < this.RENEWAL_BUFFER_MS) {
                    const minutes = Math.floor(timeRemainingMs / 60000);
                    const seconds = Math.floor((timeRemainingMs % 60000) / 1000);
                    console.log(`[Periodic Check] ${tokenType} needs renewal. Time remaining: ${minutes}m ${seconds}s`);
                    await this.renewWithRetry(tokenType);
                }
            }
            catch (error) {
                console.error(`Error in periodic check for ${tokenType}:`, error);
                this.handleRenewalError(error);
            }
        }
    }
    /** Handles Okta login redirect callback if pending */
    handleCustomCallback() {
        of(this.oktaAuthService.isLoginRedirect()).pipe(filter(isAuthenticated => !!isAuthenticated))
            .subscribe(() => {
            console.log('handling redirect');
            this.oktaAuthService.handleRedirect();
        });
    }
    /** Calculates token validity status and time remaining until expiry */
    getTokenValidityInfo(token) {
        if (!token?.expiresAt) {
            return { isValid: false, timeRemainingMs: 0 };
        }
        const now = Date.now();
        const expirationTimeMs = token.expiresAt * 1000;
        const timeRemainingMs = expirationTimeMs - now;
        return {
            isValid: timeRemainingMs > 0,
            timeRemainingMs,
            expiresAt: token.expiresAt
        };
    }
    /**
     * Ensures the token is renewed if the time elapsed since the last renewal exceeds the configured interval.
     * @returns Promise<OktaTokenInterface> - The renewed or current token.
     */
    async ensureTokenRenewal(tokenType) {
        const currentToken = await this.oktaAuthService.tokenManager.get(tokenType);
        const { isValid, timeRemainingMs } = this.getTokenValidityInfo(currentToken);
        console.log(`Token Type: ${tokenType} currentToken:`, currentToken);
        console.log(`Time remaining in ms: ${timeRemainingMs}, isValid: ${isValid}`);
        if (isValid && timeRemainingMs >= this.RENEWAL_BUFFER_MS) {
            const minutes = Math.floor(timeRemainingMs / 60000);
            const seconds = Math.floor((timeRemainingMs % 60000) / 1000);
            console.log(`Token is valid and expires in ${minutes}m ${seconds}s.`);
            return currentToken;
        }
        console.warn('Token missing, invalid, or expires soon. Renewing...');
        const newToken = await this.getNewToken(tokenType);
        return newToken;
    }
    /**
     * Schedules token renewal for a specific token type.
     * Uses separate timeout IDs to avoid cancellation issues between token types.
     */
    scheduleTokenRenewal(token, tokenType) {
        const { isValid, timeRemainingMs, expiresAt } = this.getTokenValidityInfo(token);
        if (!isValid || !expiresAt) {
            console.warn(`Cannot schedule renewal for ${tokenType}: token invalid or missing expiration.`);
            return;
        }
        const delay = timeRemainingMs - this.RENEWAL_BUFFER_MS;
        const existingTimeout = this.tokenRenewalTimeoutIds.get(tokenType);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            this.tokenRenewalTimeoutIds.delete(tokenType);
        }
        if (delay <= 0) {
            console.warn(`${tokenType} renewal time is in the past or too soon. Renewing immediately.`);
            this.renewWithRetry(tokenType);
            return;
        }
        const minutes = Math.floor(delay / 60000);
        const seconds = Math.floor((delay % 60000) / 1000);
        console.log(`Scheduling ${tokenType} renewal in ${minutes}m ${seconds}s`);
        const timeoutId = setTimeout(() => {
            this.ngZone.run(() => {
                this.renewWithRetry(tokenType);
            });
        }, delay);
        this.tokenRenewalTimeoutIds.set(tokenType, timeoutId);
    }
    /**
     * Attempts to renew a token with retry logic.
     * @param tokenType - The type of token to renew
     * @param attempt - Current attempt number (default: 1)
     */
    async renewWithRetry(tokenType, attempt = 1) {
        console.log(`Attempting ${tokenType} renewal (attempt ${attempt}/${this.MAX_RETRIES})`);
        try {
            const isAuth = await this.oktaAuthService.isAuthenticated();
            if (!isAuth) {
                console.warn('User is no longer authenticated. Cannot renew token.');
                this.handleSessionExpired();
                return;
            }
            const newToken = await this.getNewToken(tokenType);
            console.log(`${tokenType} renewed successfully on attempt ${attempt}.`);
            this.scheduleTokenRenewal(newToken, tokenType);
        }
        catch (error) {
            console.error(`${tokenType} renewal failed (attempt ${attempt}/${this.MAX_RETRIES}):`, {
                error: error,
                message: error?.message,
                errorCode: error?.errorCode
            });
            if (this.isSessionExpiredError(error)) {
                console.warn('Okta session expired. User must re-authenticate.');
                this.handleSessionExpired();
                return;
            }
            if (attempt < this.MAX_RETRIES) {
                console.log(`Retrying ${tokenType} renewal in ${this.RETRY_DELAY_MS}ms...`);
                setTimeout(() => {
                    this.ngZone.run(() => {
                        this.renewWithRetry(tokenType, attempt + 1);
                    });
                }, this.RETRY_DELAY_MS);
            }
            else {
                console.error(`All ${this.MAX_RETRIES} renewal attempts failed for ${tokenType}.`);
                this.handleRenewalError(error);
            }
        }
    }
    /** Checks if error indicates Okta session has expired (requires re-login) */
    isSessionExpiredError(error) {
        const errorMessage = error?.message || error?.errorDescription || '';
        const errorCode = error?.errorCode || '';
        return (errorMessage.includes('not to prompt') ||
            errorMessage.includes('login_required') ||
            errorMessage.includes('interaction_required') ||
            errorMessage.includes('User is not logged in') ||
            errorCode === 'login_required' ||
            errorCode === 'interaction_required');
    }
    /** Handles session expiry - clears timeouts and redirects to login */
    handleSessionExpired() {
        console.warn('Session expired. Redirecting to login...');
        this.tokenRenewalTimeoutIds.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.tokenRenewalTimeoutIds.clear();
        this.login();
    }
    /** Handles non-session-related renewal errors */
    handleRenewalError(error) {
        if (this.isSessionExpiredError(error)) {
            this.handleSessionExpired();
        }
        else {
            console.error('Token renewal failed with unrecoverable error:', error);
        }
    }
    static { this.ɵfac = function TcpOktaService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TcpOktaService)(i0.ɵɵinject(OKTA_AUTH), i0.ɵɵinject(i1.OktaAuthStateService), i0.ɵɵinject(i0.NgZone)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: TcpOktaService, factory: TcpOktaService.ɵfac, providedIn: 'root' }); }
}
//# sourceMappingURL=tcp-okta.service.js.map